/**
 * US phone normalization. We only claim to support US numbers (per spec), so we
 * strip everything but digits, drop a leading country code "1" if present, and
 * require exactly 10 digits remain. This makes "9802558167", "(980) 255-8167",
 * "980-255-8167", and "+1 980 255 8167" all resolve to the same canonical value.
 */

export interface PhoneParseResult {
  valid: boolean;
  /** Canonical storage form, e.g. "+19802558167". Only set when valid. */
  normalized?: string;
  /** Human-friendly display form, e.g. "(980) 255-8167". Only set when valid. */
  display?: string;
}

export function parsePhone(raw: string): PhoneParseResult {
  const digits = (raw ?? '').replace(/\D/g, '');

  let tenDigit = digits;
  if (digits.length === 11 && digits.startsWith('1')) {
    tenDigit = digits.slice(1);
  }

  if (tenDigit.length !== 10 || !/^[2-9]\d{9}$/.test(tenDigit)) {
    return { valid: false };
  }

  const area = tenDigit.slice(0, 3);
  const prefix = tenDigit.slice(3, 6);
  const line = tenDigit.slice(6, 10);

  return {
    valid: true,
    normalized: `+1${tenDigit}`,
    display: `(${area}) ${prefix}-${line}`,
  };
}

/** Masks a display phone for low-risk partial confirmation, e.g. "(980) 255-8167" -> "(***) ***-8167". */
export function maskPhoneDisplay(display: string): string {
  const digits = display.replace(/\D/g, '');
  if (digits.length < 4) return '(***) ***-****';
  const last4 = digits.slice(-4);
  return `(***) ***-${last4}`;
}
